import {
  ARCHETYPE_HOME_POSITIONS,
  type ArchetypeDefinition,
  type ArchetypePreview,
  type MetricDefinition,
  type PreviewArchetypeParams,
} from "@/lib/admin-api";

import { validateForm, type ArchetypeFormState } from "./archetype-form";

// The preview's rules, kept free of React (profile-spec.md §5, PF-A2b).

/** The preview runs this long after the last edit (profile-spec.md §5). */
export const PREVIEW_DEBOUNCE_MS = 400;

/** The competition the preview opens on (profile-spec.md §5). */
export const DEFAULT_COMPETITION_NAME = "Eredivisie";

const POSITION_PLURALS: Record<string, string> = {
  centre_back: "Centre-backs",
  full_back: "Full-backs",
  defensive_midfielder: "Defensive midfielders",
  central_midfielder: "Central midfielders",
  attacking_midfielder: "Attacking midfielders",
  winger: "Wingers",
  striker: "Strikers",
};

export function positionPlural(position: string): string {
  return POSITION_PLURALS[position] ?? position;
}

/**
 * True when game-service would score the definition. The name is not part
 * of a preview, so a draft with no name yet still previews.
 */
export function canPreview(
  form: ArchetypeFormState,
  metrics: ReadonlyMap<string, MetricDefinition>,
): boolean {
  return validateForm({ ...form, name: "Preview" }, metrics).length === 0;
}

/**
 * One request per home position, in display order. game-service ranks a
 * player against his own position group (profile-spec.md §9.3), so each
 * home group is its own pool, with its own top ten and its own overlap with
 * the published archetypes that share it. One request for every home would
 * merge the groups' top tens and keep only each pair's highest overlap.
 */
export function previewRequests(
  archetypeId: string,
  definition: ArchetypeDefinition,
  competitionId: string,
  seasonId: string,
): PreviewArchetypeParams[] {
  return ARCHETYPE_HOME_POSITIONS.filter((p) => definition.homePositions.includes(p)).map(
    (position) => ({
      archetypeId,
      homePositions: [position],
      weights: definition.weights,
      requiredData: definition.requiredData,
      competitionId,
      seasonId,
    }),
  );
}

/** Red text, as viz uses for text on the dark panel; the ramp's `#eb0000` is a fill colour. */
export const RED_TEXT = "#ff3b3b";
/** viz's muted text colour. */
export const MUTED_TEXT = "#a29e9e";

/**
 * Percentile text is red at the 90th or above, else muted (profile-spec.md
 * §2.7). The ramp colours bars, never text: a mid-ramp maroon does not read
 * on the dark panel.
 */
export function percentileTextColour(percentile: number | null): string {
  return percentile != null && percentile >= 90 ? RED_TEXT : MUTED_TEXT;
}

/** "0.84". Two decimals, the precision the 0.8 flag is read at. */
export function formatSpearman(r: number): string {
  if (!Number.isFinite(r)) return "";
  return r.toFixed(2);
}

export interface PreviewGroup {
  position: string;
  preview?: ArchetypePreview;
}

export interface FlaggedOverlap {
  position: string;
  archetypeId: string;
  name: string;
  spearman: number;
}

/** Every flagged pair over every home group, highest first. */
export function flaggedOverlaps(groups: readonly PreviewGroup[]): FlaggedOverlap[] {
  const out: FlaggedOverlap[] = [];
  for (const group of groups) {
    for (const overlap of group.preview?.overlaps ?? []) {
      if (!overlap.flagged) continue;
      out.push({
        position: group.position,
        archetypeId: overlap.archetypeId,
        name: overlap.name || overlap.archetypeId,
        spearman: overlap.spearman,
      });
    }
  }
  return out.sort((a, b) => b.spearman - a.spearman);
}

/**
 * True when every home group shows a settled answer for the definition as it
 * stands: none loading, failed, or still showing an earlier definition's
 * answer. Only then can an empty flag list in the publish confirm be read as
 * "no overlaps".
 */
export function overlapCheckComplete(
  groups: readonly (PreviewGroup & { loading: boolean; refreshing: boolean; error: unknown })[],
  current: boolean,
): boolean {
  if (!current || groups.length === 0) return false;
  return groups.every((g) => g.preview != null && !g.loading && !g.refreshing && !g.error);
}

/** "Final Ball, attacking midfielders, 0.95". */
export function flaggedLine(flag: FlaggedOverlap): string {
  return `${flag.name}, ${positionPlural(flag.position).toLowerCase()}, ${formatSpearman(flag.spearman)}`;
}

/** The admin page for an entity, where every entity link in admin goes. */
export function entityAdminHref(entityId: string): string {
  return `/entities/assets/entity?id=${encodeURIComponent(entityId)}`;
}
